import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const DEFAULT_HARNESS_ID = '';
const TOOL_CALL_LABEL = 'Calling tool';

function buildId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeError(error) {
  if (!error) {
    return 'Unknown error';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error.message) {
    return error.message;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
}

export function normalizeHistoryEntry(entry, index) {
  const toolType = entry.metadata?.tool?.type || entry.role;

  if (toolType === 'tool_call') {
    return {
      id: entry.id || entry.message_id || `history-${index}`,
      kind: 'tool',
      message: entry.message || `${TOOL_CALL_LABEL}: ${entry.metadata?.tool?.name || 'unknown'}`,
      messageId: entry.message_id,
      owner: 'agent',
      state: 'tool',
    };
  }

  if (toolType === 'tool_result') {
    return {
      id: entry.id || entry.message_id || `history-${index}`,
      kind: 'tool',
      message: entry.message || `Tool result: ${entry.metadata?.tool?.name || 'unknown'}`,
      messageId: entry.message_id,
      owner: 'tool',
      state: 'tool',
    };
  }

  const message = entry.message || entry.error || '';
  const historyOwner = entry.owner || entry.metadata?.display?.owner || entry.role;
  const owner = historyOwner === 'user' ? 'user' : 'agent';
  const state = entry.error ? 'error' : 'complete';

  return {
    id: entry.id || entry.message_id || `history-${index}`,
    kind: entry.error ? 'error' : 'message',
    message,
    messageId: entry.message_id,
    owner,
    state,
  };
}

function appendToMessage(entries, messageId, message) {
  return entries.map((entry) => {
    if (entry.messageId !== messageId || entry.kind !== 'message') {
      return entry;
    }

    return {
      ...entry,
      message: `${entry.message}${message || ''}`,
    };
  });
}

function completeMessage(entries, messageId, message) {
  return entries.map((entry) => {
    if (entry.messageId !== messageId || entry.kind !== 'message') {
      return entry;
    }

    return {
      ...entry,
      message: message || entry.message,
      state: 'complete',
    };
  });
}

function addAgentPlaceholder(entries, messageId) {
  if (!messageId) {
    return entries;
  }

  if (entries.some((entry) => entry.messageId === messageId)) {
    return entries;
  }

  return [
    ...entries,
    {
      id: messageId || buildId('agent'),
      kind: 'message',
      message: '',
      messageId,
      owner: 'agent',
      state: 'streaming',
    },
  ];
}

function markEntryFailed(entries, entryId, message) {
  return entries.map((entry) => {
    if (entry.id !== entryId) {
      return entry;
    }

    return {
      ...entry,
      kind: 'error',
      message: `${entry.message}\n\nFailed to send: ${message}`,
      state: 'error',
    };
  });
}

function appendErrorEntry(entries, messageId, message) {
  if (entries.some((entry) => entry.kind === 'error' && (entry.messageId === messageId || entry.message === message))) {
    return entries;
  }

  return [
    ...entries,
    {
      id: messageId || buildId('error'),
      kind: 'error',
      message,
      messageId,
      owner: 'agent',
      state: 'error',
    },
  ];
}

export function chatListEntries(response) {
  if (Array.isArray(response)) {
    return response;
  }

  return (
    response?.entries ||
    response?.history ||
    response?.messages ||
    response?.chats ||
    response?.sessions ||
    response?.data ||
    []
  );
}

export function chatListNextCursor(response) {
  return response?.next_cursor || response?.nextCursor || response?.pagination?.next_cursor || null;
}

export function useHarnessChat(ai) {
  const location = useLocation();
  const isMountedRef = useRef(true);
  const loadSessionsRequestRef = useRef(0);
  const loadChatRequestRef = useRef(0);
  const sendRequestRef = useRef(0);
  const [harnessId, setHarnessId] = useState(DEFAULT_HARNESS_ID);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsNextCursor, setSessionsNextCursor] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [error, setError] = useState(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const clientTools = useMemo(
    () => ({
      get_browser: () => ({
        language: navigator.language,
        url: `${location.pathname}${location.search}${location.hash}`,
        userAgent: navigator.userAgent,
        viewport: {
          height: window.innerHeight,
          width: window.innerWidth,
        },
      }),
    }),
    [location.hash, location.pathname, location.search],
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      loadSessionsRequestRef.current += 1;
      loadChatRequestRef.current += 1;
      sendRequestRef.current += 1;
    };
  }, []);

  const loadSessions = useCallback(
    async (cursor = null) => {
      if (!ai?.chatList || !harnessId) {
        setSessions([]);
        setSessionsNextCursor(null);
        return;
      }

      const requestId = (loadSessionsRequestRef.current += 1);
      setIsLoadingSessions(true);
      setError(null);

      try {
        const response = await ai.chatList(harnessId, cursor ? { cursor } : undefined);

        if (!isMountedRef.current || requestId !== loadSessionsRequestRef.current) {
          return;
        }

        const entries = chatListEntries(response);
        setSessions((currentSessions) => (cursor ? [...currentSessions, ...entries] : entries));
        setSessionsNextCursor(chatListNextCursor(response));
      } catch (e) {
        if (isMountedRef.current && requestId === loadSessionsRequestRef.current) {
          setError(normalizeError(e));
        }
      } finally {
        if (isMountedRef.current && requestId === loadSessionsRequestRef.current) {
          setIsLoadingSessions(false);
        }
      }
    },
    [ai, harnessId],
  );

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const startNewChat = useCallback(() => {
    setCurrentChatId(null);
    setTranscript([]);
    setError(null);
  }, []);

  const selectHarness = useCallback((nextHarnessId) => {
    setHarnessId(nextHarnessId);
    setCurrentChatId(null);
    setSessions([]);
    setSessionsNextCursor(null);
    setTranscript([]);
    setError(null);
  }, []);

  const loadChat = useCallback(
    async (chatId) => {
      if (!ai?.chatRetrieve || !chatId) {
        return;
      }

      const requestId = (loadChatRequestRef.current += 1);
      setIsLoadingChat(true);
      setError(null);

      try {
        const response = await ai.chatRetrieve(chatId);

        if (!isMountedRef.current || requestId !== loadChatRequestRef.current) {
          return;
        }

        const entries = chatListEntries(response);

        setCurrentChatId(chatId);
        setTranscript(entries.map(normalizeHistoryEntry));
      } catch (e) {
        if (isMountedRef.current && requestId === loadChatRequestRef.current) {
          setError(normalizeError(e));
        }
      } finally {
        if (isMountedRef.current && requestId === loadChatRequestRef.current) {
          setIsLoadingChat(false);
        }
      }
    },
    [ai],
  );

  const sendMessage = useCallback(
    async (message) => {
      const cleanMessage = message.trim();

      if (!ai?.chat || !harnessId || !cleanMessage || isSending) {
        return;
      }

      setIsSending(true);
      setError(null);
      const requestId = (sendRequestRef.current += 1);
      const userEntryId = buildId('user');
      let handledStreamError = false;
      setTranscript((entries) => [
        ...entries,
        {
          id: userEntryId,
          kind: 'message',
          message: cleanMessage,
          owner: 'user',
          state: 'complete',
        },
      ]);

      try {
        const reply = await ai.chat(harnessId, cleanMessage, {
          chatId: currentChatId,
          clientTools,
          onChunk: (payload) => {
            if (!isMountedRef.current || requestId !== sendRequestRef.current) {
              return;
            }

            const messageId = payload?.message_id;
            setTranscript((entries) =>
              appendToMessage(addAgentPlaceholder(entries, messageId), messageId, payload?.message),
            );
          },
          onError: (payload) => {
            if (!isMountedRef.current || requestId !== sendRequestRef.current) {
              return;
            }

            handledStreamError = true;
            const messageId = payload?.message_id;
            const payloadError = payload?.error || payload;
            const messageText = normalizeError(payloadError);

            if (!messageId) {
              setTranscript((entries) => markEntryFailed(entries, userEntryId, messageText));
              setError(messageText);
              return;
            }

            setTranscript((entries) => appendErrorEntry(entries, messageId, messageText));
            setError(messageText);
          },
          onResponse: (payload) => {
            if (!isMountedRef.current || requestId !== sendRequestRef.current) {
              return;
            }

            const messageId = payload?.message_id;
            setTranscript((entries) =>
              completeMessage(addAgentPlaceholder(entries, messageId), messageId, payload?.message),
            );
          },
          onToolCall: (payload) => {
            if (!isMountedRef.current || requestId !== sendRequestRef.current) {
              return;
            }

            const toolName = payload?.name || payload?.tool || payload?.metadata?.tool?.name || 'client tool';
            setTranscript((entries) => [
              ...entries,
              {
                id: buildId('tool'),
                kind: 'tool',
                message: payload?.message || `${TOOL_CALL_LABEL}: ${toolName}`,
                messageId: payload?.message_id,
                owner: 'agent',
                state: 'tool',
              },
            ]);
          },
        });

        if (!isMountedRef.current || requestId !== sendRequestRef.current) {
          return;
        }

        if (!reply && handledStreamError) {
          return;
        }

        if (reply?.chat_id) {
          setCurrentChatId(reply.chat_id);
        }

        if (reply?.message_id) {
          setTranscript((entries) => addAgentPlaceholder(entries, reply.message_id));
        }

        loadSessions();
      } catch (e) {
        if (!isMountedRef.current || requestId !== sendRequestRef.current) {
          return;
        }

        const messageText = normalizeError(e);
        setError(messageText);

        if (!handledStreamError) {
          setTranscript((entries) => markEntryFailed(entries, userEntryId, messageText));
        }
      } finally {
        if (isMountedRef.current && requestId === sendRequestRef.current) {
          setIsSending(false);
        }
      }
    },
    [ai, clientTools, currentChatId, harnessId, isSending, loadSessions],
  );

  const loadMoreSessions = useCallback(() => {
    if (!sessionsNextCursor || isLoadingSessions) {
      return;
    }

    loadSessions(sessionsNextCursor);
  }, [isLoadingSessions, loadSessions, sessionsNextCursor]);

  return {
    currentChatId,
    error,
    harnessId,
    isLoadingChat,
    isLoadingSessions,
    isSending,
    loadChat,
    loadMoreSessions,
    refreshSessions: loadSessions,
    sendMessage,
    sessions,
    sessionsNextCursor,
    setHarnessId: selectHarness,
    startNewChat,
    transcript,
  };
}
